import { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'utils/yup';
import { getDays, getMonths, isValidDay, getBirthdayYearOptions } from 'utils/date';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { SupportUserQuery } from 'interfaceAdapter/queries/SupportUser';
import { SupportUserRepository } from 'interfaceAdapter/repositories/SupportUser';
import { supportUserContext } from 'application/contexts/useSupportUser';
import { useNavigate } from 'react-router-dom';
import { useLoading } from 'application/contexts/useLoading';

export const schema = yup.object().shape({
    first_name: yup.string().required().max(50),
    last_name: yup.string().required().max(50),
    first_name_kana: yup.string().required().max(50),
    last_name_kana: yup.string().required().max(50),
    prefecture: yup.string().required(),
    age: yup.string().required(),
    industry: yup.string().max(200),
    company_name: yup.string().max(200),
    contact_method: yup.string().max(100),
    business_experience: yup.string().max(100),
    icon_image: yup.string().nullable().defined(),
    department: yup.string().nullable().defined().default(''),
    job: yup.string().nullable().defined().default(''),
    gender: yup.number().required(),
    phone_number: yup.string().required().max(30).onlyNumber().defined(),
    year: yup.number().required(),
    month: yup.number().required(),
    day: yup.number().required(),
    teamsAgreed: yup.boolean().required().oneOf([true], 'サポーター利用規約に同意してください'),
});

type SupportUserForm = yup.InferType<typeof schema>;

const ageOptions = ['10代', '20代', '30代', '40代', '50代', '60代', '70代', '80代〜'];

// 事業承継やM&Aへの関わり経験
const businessExperienceOptions = [
    '実務として深く関わっている（現在）',
    '実務として深く関わっていた（過去）',
    'ある程度関与している',
    '関心はあるが実務経験は少ない',
    'ほぼ関わりはない',
    'その他',
];

// ご希望の連絡方法
const contactMethodOptions = ['メール', '電話', 'どちらでも可'];

export const useSupportUserProfileRegistration = () => {
    const diContainer = useContext(diContainerContext);
    const supportUserQuery = diContainer.resolve(SupportUserQuery);
    const supportUserRepository = diContainer.resolve(SupportUserRepository);
    const { loading, setLoading } = useLoading();
    const [showSnackbar, setShowSnackbar] = useState(false);
    const { user, setUser } = useContext(supportUserContext);
    const navigate = useNavigate();

    const {
        control,
        handleSubmit,
        formState: { errors },
        getValues,
        watch,
        setValue,
        setError,
        clearErrors,
    } = useForm<SupportUserForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            icon_image: null,
            year: 1920,
            month: 1,
            day: 1,
            job: '',
            department: '',
        },
    });

    useEffect(() => {
        const checkCanEnter = async () => {
            const { user, status } = await supportUserQuery.checkToken(false);

            if (status === false) {
                // 有効なトークンが無いのでリダイレクト
                navigate('/supporter/login/');
            }
            setUser(user);
        };
        checkCanEnter();
    }, [window.location]);

    useEffect(() => {
        if (user) {
            setValue('first_name', user.first_name);
            setValue('last_name', user.last_name);
            setValue('first_name_kana', user.first_name_kana ?? '');
            setValue('last_name_kana', user.last_name_kana ?? '');
            setValue('prefecture', user.prefecture ?? '');
            setValue('age', user.age ?? '');
            setValue('industry', user.industry ?? '');
            setValue('company_name', user.company_name ?? '');
            setValue('contact_method', user.contact_method ?? '');
            setValue('business_experience', user.business_experience ?? '');
            setValue('icon_image', user.icon_image);
            setValue('department', user.department);
            setValue('job', user.job);
            setValue('gender', user.gender);
            setValue('phone_number', user.phone_number ?? '');
            if (user.birthday) {
                const [year, month, day] = user.birthday.split('-').map((v) => parseInt(v, 10));
                setValue('year', year);
                setValue('month', month);
                setValue('day', day);
            }
        }
    }, [user]);

    const submit = handleSubmit(async (data) => {
        setLoading(true);
        try {
            const birthday = `${data.year}-${data.month}-${data.day}`;

            await supportUserRepository.editProfile({
                ...data,
                birthday,
            });
            setShowSnackbar(true);
            setTimeout(() => (window.location.href = '/supporter/dashboard/'), 1500);
        } finally {
            setLoading(false);
        }
    });

    useEffect(() => {
        // 生年月日の組み合わせに対するバリデーション
        const subscription = watch((value, { name }) => {
            if (!['year', 'month', 'day'].includes(name ?? '')) {
                return;
            }
            const { year = -1, month = -1, day = -1 } = value;
            if (value.year === -1 || value.month === -1 || value.day === -1) {
                return;
            }
            if (!isValidDay(year, month, day)) {
                // 便宜上yearのエラーとして登録
                setError('year', { message: '正しい日付を入力してください。' });
            } else {
                clearErrors('year');
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    const yearOptions = getBirthdayYearOptions();
    const monthOptions = getMonths();
    const dayOptions = getDays();

    return {
        control,
        errors,
        loading,
        showSnackbar,
        yearOptions,
        monthOptions,
        dayOptions,
        ageOptions,
        businessExperienceOptions,
        contactMethodOptions,
        setShowSnackbar,
        submit,
        getValues,
        setValue,
        watch,
    };
};
